import {
  ExtraCO2Types,
  ExtraCO2TypeReqBody,
  extraCO2TypesT,
  extraCo2TypeReqBodyT,
  ExtraCO2Type,
  ExtraCo2TypeUsage,
  extraCo2TypeInUseResT,
} from "dora-contracts";
import { createErrorReportingAsyncThunk } from "../../helpers";
import axios from "../../../axios";
import * as tPromise from "io-ts-promise";
import { createSlice } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import {
  ERR_DELETED_EXTRA_CO2_TYPES,
  ERR_DUPLICATE_EXTRA_CO2_TYPE,
} from "dora-shared";

const prefix = "data/extra-co2-types";

interface State {
  types: ExtraCO2Types;
}

const initialState: State = {
  types: [],
};

export const loadExtraCO2Types = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async () => {
    const { data } = await axios.get("/api/extra-co2-types");
    return tPromise.decode(extraCO2TypesT, data);
  }
);

// A duplicate name is an expected refusal the dialog shows on the name field,
// so it is returned rather than thrown: throwing would send it through
// createErrorReportingAsyncThunk's Sentry report and generic error toast.
type SaveExtraCO2TypeResult = {
  error: typeof ERR_DUPLICATE_EXTRA_CO2_TYPE | null;
};

const saveExtraCO2Type = async (
  request: () => Promise<unknown>
): Promise<SaveExtraCO2TypeResult> => {
  try {
    await request();
  } catch (e) {
    if (isAxiosError(e) && e.response?.status === 409) {
      return { error: ERR_DUPLICATE_EXTRA_CO2_TYPE };
    }
    throw e;
  }
  return { error: null };
};

export const createExtraCO2Type = createErrorReportingAsyncThunk(
  `${prefix}/create`,
  async (data: ExtraCO2TypeReqBody, { dispatch }) => {
    const result = await saveExtraCO2Type(() =>
      axios.post("/api/extra-co2-types", extraCo2TypeReqBodyT.encode(data))
    );
    if (!result.error) {
      dispatch(loadExtraCO2Types());
    }
    return result;
  }
);

export const updateExtraCO2Type = createErrorReportingAsyncThunk(
  `${prefix}/update`,
  async (data: ExtraCO2Type, { dispatch }) => {
    const result = await saveExtraCO2Type(() =>
      axios.put(
        `/api/extra-co2-types/${data.id}`,
        extraCo2TypeReqBodyT.encode(data)
      )
    );
    if (!result.error) {
      dispatch(loadExtraCO2Types());
    }
    return result;
  }
);

// A type still in use isn't deleted until the user confirms: the server
// answers 409 with where it's used, which is returned for the dialog to show
// rather than thrown (that would report it and show the error toast).
type DeleteExtraCO2TypeResult =
  | { type: "DELETED" }
  | { type: "IN_USE"; usage: ExtraCo2TypeUsage };

export const deleteExtraCO2Type = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (
    input: { id: string; confirmDeleteUsed: boolean },
    { dispatch }
  ): Promise<DeleteExtraCO2TypeResult> => {
    try {
      await axios.delete(`/api/extra-co2-types/${input.id}`, {
        params: input.confirmDeleteUsed
          ? { confirmDeleteUsed: true }
          : undefined,
      });
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 409) {
        const { usage } = await tPromise.decode(
          extraCo2TypeInUseResT,
          e.response.data
        );
        return { type: "IN_USE", usage };
      }
      throw e;
    }
    dispatch(loadExtraCO2Types());
    return { type: "DELETED" };
  }
);

/**
 * A cargo or template save that adds lines whose extra CO2 type was deleted is
 * refused with this until the user confirms saving without them; the save is
 * then resent with `dropDeletedExtraCo2TypesParam`.
 */
export const isDeletedExtraCo2TypesError = (e: unknown) =>
  isAxiosError(e) &&
  e.response?.status === 409 &&
  e.response.data?.message === ERR_DELETED_EXTRA_CO2_TYPES;

export const dropDeletedExtraCo2TypesParam = (drop: boolean | undefined) =>
  drop ? { dropDeletedExtraCo2Types: true } : {};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadExtraCO2Types.fulfilled, (state, action) => {
      state.types = action.payload;
    });
  },
});

export default slice.reducer;
